import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/** Missing param → `fallback`. Present empty string → `[]` (nothing selected). */
export function parseUrlFilterParam(raw: string | null, fallback: readonly string[] = []): string[] {
  if (raw == null) return [...fallback];
  if (raw === '') return [];
  return raw.split(',').filter(Boolean);
}

/**
 * Persist a multi-select filter in the URL (`?key=a,b`).
 * Missing param → `fallback`. Present empty string → `[]` (nothing selected).
 */
export function useUrlFilterState(
  key: string,
  fallback: readonly string[] = [],
): [string[], (next: string[]) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(key);
  const fallbackKey = fallback.join('\0');

  const value = useMemo(() => {
    return parseUrlFilterParam(raw, fallback);
    // fallback is represented by fallbackKey so callers can pass inline arrays.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw, fallbackKey]);

  const setValue = useCallback(
    (next: string[]) => {
      setParams((prev) => {
        const nextParams = new URLSearchParams(prev);
        if (next.length === 0) nextParams.set(key, '');
        else nextParams.set(key, next.join(','));
        return nextParams;
      }, { replace: true });
    },
    [key, setParams],
  );

  return [value, setValue];
}
